import { useEffect, useMemo, useState } from "react";

export type PaginationOptions = {
  /** Rows per page to start with. Defaults to 25. */
  pageSize?: number;
  /**
   * Any value that should send the user back to page 1 when it changes —
   * typically the search text and active filters. Kept as a dependency only,
   * so pass a primitive (string/number) or a memoised value.
   */
  resetKey?: unknown;
};

/**
 * Client-side pagination for a list that is already fully loaded.
 *
 * Usage:
 *   const pager = usePagination(filteredRows, { resetKey: `${search}|${status}` });
 *   pager.pageItems.map((row, i) => ...)          // rows for the current page
 *   pager.startIndex + i                           // absolute index, for index-based handlers
 *   <TablePagination {...pager.paginationProps} />
 *
 * `page` is 1-based. The page snaps back into range when the list shrinks
 * (search, filter, delete), so callers never render an empty page.
 */
export function usePagination<T>(items: T[] | null | undefined, options: PaginationOptions = {}) {
  const list = items ?? [];
  const [page, setPageRaw] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(options.pageSize ?? 25);

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== currentPage) setPageRaw(currentPage);
  }, [page, currentPage]);

  useEffect(() => {
    setPageRaw(1);
  }, [options.resetKey]);

  const startIndex = (currentPage - 1) * pageSize;
  const pageItems = useMemo(
    () => list.slice(startIndex, startIndex + pageSize),
    [list, startIndex, pageSize]
  );

  const setPage = (p: number) => setPageRaw(Math.min(Math.max(1, p), totalPages));
  const setPageSize = (size: number) => {
    setPageSizeRaw(size);
    setPageRaw(1);
  };

  return {
    page: currentPage,
    setPage,
    pageSize,
    setPageSize,
    total,
    totalPages,
    startIndex,
    pageItems,
    paginationProps: {
      page: currentPage,
      pageSize,
      total,
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
    },
  };
}

export default usePagination;
